<template>
  <div
    v-if="loading"
    class="_loading-container"
  >
    <m-spinner />
  </div>
  <div
    v-else-if="error !== null"
    class="_alert"
  >
    <m-alert
      type="error"
      :message="error"
    />
  </div>
  <MetricDisplay
    v-else-if="chart.chartType === gridPageChartType.metric"
    :value="metricResult"
    :unit="metricUnit"
    :loading="loading"
    :size="size"
  />
  <chart
    v-else
    :key="chart.chartType"
    :options="chartOptions"
    class="_chart"
  />
</template>

<script setup>
import MetricDisplay from '@/components/custom-grid/chart/MetricDisplay.vue';
import useBarChart from '@/composables/grid-page/chart/bar-chart';
import useChartDataQuery from '@/composables/chart-data-query';
import useLineChart from '@/composables/grid-page/chart/line-chart';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import usePieChart from '@/composables/grid-page/chart/pie-chart';
import { Chart } from 'highcharts-vue';
import { aggregatorType, gridPageChartType } from 'shared/constants.json';
import { computed, toRef } from 'vue';
import { edgeName } from '@/composables/grid-page/chart/utils';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  chart: { type: Object, required: true },
  size: { type: String, default: 'medium', validator: (value) => ['small', 'medium', 'large'].includes(value) },
});

const { loading, result, error } = useChartDataQuery(toRef(props, 'chart'));

const { chartOptions: pieChartOptions } = usePieChart(result, toRef(props, 'chart'));
const { chartOptions: lineChartOptions } = useLineChart(result, toRef(props, 'chart'));
const { chartOptions: barChartOptions } = useBarChart(result, toRef(props, 'chart'));

const { userLang } = useLoggedInUser();

const chartOptions = computed(() => {
  const baseOptions = { chart: { animation: false } };

  switch (props.chart.chartType) {
    case gridPageChartType.column:
    case gridPageChartType.bar:
      return barChartOptions.value;
    case gridPageChartType.line:
      return lineChartOptions.value;
    case gridPageChartType.pie:
      return pieChartOptions.value;
    default:
      return baseOptions;
  }
});

const metricResult = computed(() => {
  if (props.chart.chartType !== gridPageChartType.metric || result.value === null) {
    return null;
  }

  return Number(result.value.toFixed(2));
});

const { t } = useI18n();

const metricUnit = computed(() => {
  if (props.chart.chartType !== gridPageChartType.metric) {
    return null;
  }

  switch (props.chart.aggregationConfig.aggregation.aggregator) {
    case aggregatorType.count: {
      return t(`gridPageChart.${props.chart.source}`);
    }
    case aggregatorType.sum: {
      return `${t('gridPageChart.sum', { edge: edgeName(props.chart.aggregationConfig.aggregation, t, userLang.value) })}`;
    }
    case aggregatorType.avg: {
      return `${t('gridPageChart.avg', { edge: edgeName(props.chart.aggregationConfig.aggregation, t, userLang.value) })}`;
    }
    case aggregatorType.min: {
      return `${t('gridPageChart.min', { edge: edgeName(props.chart.aggregationConfig.aggregation, t, userLang.value) })}`;
    }
    case aggregatorType.max: {
      return `${t('gridPageChart.max', { edge: edgeName(props.chart.aggregationConfig.aggregation, t, userLang.value) })}`;
    }
    case aggregatorType.countDistinct: {
      return `${t('gridPageChart.countDistinct', { edge: edgeName(props.chart.aggregationConfig.aggregation, t, userLang.value) })}`;
    }
    default:
      return null;
  }
});
</script>

<style scoped lang="scss">
._alert {
  width: 100%;
}

._chart {
  width: 100%;
  height: 100%;
}

._loading-container {
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
